import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { vmsService } from '../../services/vmsService';
import { userService } from '../../services/userService';

export const VendorComparison: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'vendor' | 'product'>('vendor');

  const [departments, setDepartments] = useState<any[]>([]);
  const [vendorTypes, setVendorTypes] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  const [departmentId, setDepartmentId] = useState<string>('');
  const [vendorTypeId, setVendorTypeId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [activePreset, setActivePreset] = useState<string>('');

  useEffect(() => {
    userService.getDepartments().then(res => setDepartments(res.results || res)).catch(console.error);
    vmsService.getVendorTypes().then(res => setVendorTypes(res.results || res)).catch(console.error);
  }, []);

  useEffect(() => {
    // If not super admin, wait for departmentId to be set before fetching to avoid redundant call
    if (!isSuperAdmin && !departmentId) return;
    vmsService.getProducts(departmentId || undefined).then(res => setProducts(res.results || res)).catch(console.error);
  }, [departmentId, isSuperAdmin]);

  useEffect(() => {
    if (currentUser && !isSuperAdmin && departments.length > 0 && currentUser.department) {
      const dept = departments.find(d => d.name === currentUser.department);
      if (dept) setDepartmentId(String(dept.id));
    }
  }, [currentUser, isSuperAdmin, departments]);

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await vmsService.getVendorComparison({
        department_id: departmentId,
        vendor_type_id: vendorTypeId,
        state,
        date_from: dateFrom,
        date_to: dateTo,
        product_id: activeTab === 'product' ? productId : undefined
      });
      setData(res);
    } catch (err) {
      console.error(err);
      alert('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, vendorTypeId, state, dateFrom, dateTo, productId, activeTab]);

  // Quick Filters
  const applyPreset = (type: string) => {
    setActivePreset(type);
    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (type === 'FY_CURRENT') {
      const currentYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      start = new Date(currentYear, 3, 1); // April 1
      end = new Date(currentYear + 1, 2, 31); // March 31
    } else if (type === 'FY_PREV') {
      const currentYear = today.getMonth() >= 3 ? today.getFullYear() - 1 : today.getFullYear() - 2;
      start = new Date(currentYear, 3, 1);
      end = new Date(currentYear + 1, 2, 31);
    } else if (type === 'CY_CURRENT') {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    } else if (type === 'MONTH_CURRENT') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (type === 'CLEAR') {
      setDateFrom('');
      setDateTo('');
      if (isSuperAdmin) setDepartmentId('');
      setVendorTypeId('');
      setProductId('');
      setState('');
      return;
    }

    if (type !== 'CLEAR') {
      setDateFrom(start.toISOString().split('T')[0]);
      setDateTo(end.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => navigate(-1)} style={{ color: 'var(--text-dark)', display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title="Back">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>Analytics Dashboard</h1>
          </div>
        </div>
        <p className="page-description" style={{ margin: 0, marginTop: '0.5rem' }}>Compare vendors by total sales, invoices generated, and specific product pricing.</p>
      </div>

      <div className="tabs-container" style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem', marginTop: '1.5rem' }}>
        <button
          className={`tab-button ${activeTab === 'vendor' ? 'active' : ''}`}
          onClick={() => setActiveTab('vendor')}
          style={{ padding: '0.75rem 1.5rem', fontWeight: 600, borderBottom: activeTab === 'vendor' ? '2px solid var(--primary)' : 'none', color: activeTab === 'vendor' ? 'var(--primary)' : 'var(--text-light)', background: 'none', cursor: 'pointer', fontSize: '1rem' }}
        >
          Vendor Analytics
        </button>
        <button
          className={`tab-button ${activeTab === 'product' ? 'active' : ''}`}
          onClick={() => setActiveTab('product')}
          style={{ padding: '0.75rem 1.5rem', fontWeight: 600, borderBottom: activeTab === 'product' ? '2px solid var(--primary)' : 'none', color: activeTab === 'product' ? 'var(--primary)' : 'var(--text-light)', background: 'none', cursor: 'pointer', fontSize: '1rem' }}
        >
          Product Comparison
        </button>
      </div>

      <div className="actions-header">
        {/* Quick Presets */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)', marginRight: '0.5rem' }}>Quick Filters:</span>
          <button className={`${activePreset === 'FY_CURRENT' ? 'primary-btn' : 'secondary-btn'} sm-btn`} onClick={() => applyPreset('FY_CURRENT')}>Current FY</button>
          <button className={`${activePreset === 'FY_PREV' ? 'primary-btn' : 'secondary-btn'} sm-btn`} onClick={() => applyPreset('FY_PREV')}>Prev FY</button>
          <button className={`${activePreset === 'CY_CURRENT' ? 'primary-btn' : 'secondary-btn'} sm-btn`} onClick={() => applyPreset('CY_CURRENT')}>Current CY</button>
          <button className={`${activePreset === 'MONTH_CURRENT' ? 'primary-btn' : 'secondary-btn'} sm-btn`} onClick={() => applyPreset('MONTH_CURRENT')}>This Month</button>
          <button className={`${activePreset === 'CLEAR' ? 'primary-btn' : 'secondary-btn'} sm-btn`} onClick={() => applyPreset('CLEAR')}>Clear All</button>
        </div>
      </div>

      <div className="filter-panel" style={{ marginTop: '0', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          {isSuperAdmin && (
            <div className="filter-group">
              <label>Department</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'product' && (
            <div className="filter-group">
              <label>Product Catalog</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Select a Product...</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Vendor Type</label>
            <select value={vendorTypeId} onChange={(e) => setVendorTypeId(e.target.value)}>
              <option value="">All Types</option>
              {vendorTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Location (State)</label>
            <select value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">All States</option>
              {[
                "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
                "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", 
                "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", 
                "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
                "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", 
                "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir"
              ].map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <label>Date From</label>
            <input type="date" value={dateFrom} onChange={(e) => { setActivePreset(''); setDateFrom(e.target.value); }} />
          </div>

          <div className="filter-group">
            <label>Date To</label>
            <input type="date" value={dateTo} onChange={(e) => { setActivePreset(''); setDateTo(e.target.value); }} />
          </div>
        </div>

      <div className="list-container" style={{ marginTop: '1.5rem' }}>
        {loading ? (
          <p style={{ padding: '1rem' }}>Loading comparison data...</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Vendor Code</th>
                <th>Name</th>
                <th>Department</th>
                <th>Vendor Type</th>
                {activeTab === 'product' && <th>Quoted Price</th>}
                {activeTab === 'product' && <th>Quoted Quantity</th>}
                <th>Invoices Generated</th>
                <th>Total Sales</th>
                <th>Amount Due</th>
              </tr>
            </thead>
            <tbody>
              {data.map(v => (
                <tr key={v.id}>
                  <td><strong>{v.vendor_code}</strong></td>
                  <td>{v.name}</td>
                  <td>{v.department || 'N/A'}</td>
                  <td>{v.vendor_type}</td>
                  {activeTab === 'product' && <td style={{ color: 'var(--primary)', fontWeight: 600 }}>{v.quoted_price ? `₹${v.quoted_price}` : 'N/A'}</td>}
                  {activeTab === 'product' && <td>{v.quantity || 'N/A'}</td>}
                  <td>{v.invoices_generated}</td>
                  <td style={{ color: 'var(--primary)', fontWeight: 600 }}>₹{v.total_sales.toFixed(2)}</td>
                  <td style={{ color: '#d97706', fontWeight: 600 }}>₹{v.amount_due.toFixed(2)}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={activeTab === 'product' ? 9 : 7} style={{ textAlign: 'center', padding: '2rem' }}>No vendors found for the given criteria.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
